import { useRef, type KeyboardEvent } from "react";

const TYPING_BURST_MS = 1000;
const MAX_STEPS = 100;

function isTypingStep(prev: unknown, next: unknown): boolean {
  if (typeof prev !== "string" || typeof next !== "string") return false;
  if (Math.abs(next.length - prev.length) !== 1) return false;
  return next.startsWith(prev) || prev.startsWith(next);
}

export function useFieldUndo<T>(value: T, apply: (value: T) => void) {
  const past = useRef<T[]>([]);
  const future = useRef<T[]>([]);
  const lastEdit = useRef(0);

  const record = (next: T) => {
    if (Object.is(next, value)) return;
    const now = Date.now();
    const continuing = now - lastEdit.current < TYPING_BURST_MS && isTypingStep(value, next);
    if (!continuing) {
      past.current.push(value);
      if (past.current.length > MAX_STEPS) past.current.shift();
    }
    future.current = [];
    lastEdit.current = now;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key !== "z" && key !== "y") return;
    const redo = key === "y" || e.shiftKey;
    const from = redo ? future.current : past.current;
    const to = redo ? past.current : future.current;
    if (from.length === 0) return;
    e.preventDefault();
    to.push(value);
    lastEdit.current = 0;
    apply(from.pop() as T);
  };

  return { record, onKeyDown };
}
